/* eslint-disable no-unused-vars */
import React, { memo, useContext, useEffect, useReducer } from 'react';
import PropTypes from 'prop-types';
import { NavLink, useHistory, useParams } from 'react-router-dom';
import { Accordion, AccordionDetails, AccordionSummary, Button, Drawer } from '@material-ui/core';
import DashboardIcon from '@material-ui/icons/Dashboard';
import TimelineIcon from '@material-ui/icons/Timeline';
import WebIcon from '@material-ui/icons/Web';
import SettingsIcon from '@material-ui/icons/Settings';
import ListAltIcon from '@material-ui/icons/ListAlt';
import LiveHelpIcon from '@material-ui/icons/LiveHelp';
import AttachMoneyIcon from '@material-ui/icons/AttachMoney';
import SportsRugbyIcon from '@material-ui/icons/SportsRugby';
import { context } from '../../../../globalState/GlobalState';
import './css/ExpandedDrawer.css';
import { isValidObject, isValidString } from '../../../../catalog/Validity';
import { ANALYTICS_NAVLINKS, ARTICLES_NAVLINKS, CUSTOM_CONTENTS_NAVLINKS, EVENTS_NAVLINKS, FORMS_NAVLINKS, GALLERIES_NAVLINKS, ROSTER_NAVLINKS, SETTINGS_NAVLINKS, STORE_NAVLINKS, HELP_NAVLINKS, WEBSITE_NAVLINKS } from '../../../../catalog/NavLinks';
import TooltipComponent3 from '../../../portal/uiElements/tooltipComponents/TooltipComponent3';
import { WebsiteType } from '../../../../catalog/enums/WebsiteType';
import { getWebsiteURL } from '../../../../catalog/Others';
import { SECTION_ICONS } from '../../../portal/uiElements/editorLayout/sectionIcons';
import useNCRClaimEligibility from '../../../portal/ncr/useNCRClaimEligibility';
import { NCR_PORTAL_LINK } from '../../../../catalog/NCR';

const initialState = {
    expandedAccordian: '/portal/dashboard',
};

function reducer(state, action) {
    switch (action.type) {
        case 'setExpandedAccordian':
            return { ...state, expandedAccordian: action.expandedAccordian };
        default:
            return initialState;
    }
}

const ExpandedDrawer = (props) => {
    const { children } = props;
    const [state, setState] = useReducer(reducer, initialState);
    const history = useHistory();
    const params = useParams();
    const { globalState } = useContext(context);
    const { eligible: ncrClaimEligible } = useNCRClaimEligibility();

    useEffect(() => {
        if (history.location.pathname !== state.expandedAccordian && history.location.pathname !== '/') {
            setState({ type: 'setExpandedAccordian', expandedAccordian: history.location.pathname });
        }
    }, [history.location.pathname, params]);

    // Unsubscribed rugby teams without a domain get an NCR page (claim the NCR free year) above Help.
    const ncrNavItem = ncrClaimEligible ? [{ title: 'NCR', icon: <SportsRugbyIcon className="m-r-20px" />, link: NCR_PORTAL_LINK }] : [];

    const getNavItems = () => {
        if (globalState.websiteType === WebsiteType.STORE) {
            return [
                { title: 'Dashboard', icon: <DashboardIcon className="m-r-20px" />, link: '/portal/dashboard' },
                { title: 'Analytics', icon: <TimelineIcon className="m-r-20px" />, link: '/portal/analytics/store' },
                { title: 'Website', icon: <WebIcon className="m-r-20px" />, link: '/portal/website', navItems: WEBSITE_NAVLINKS(globalState.websiteType) },
                { title: 'Store, Tickets & Registration Forms', icon: <AttachMoneyIcon className="m-r-20px" />, link: '/portal/store', navItems: isValidString(globalState.stripeAccountId) ? STORE_NAVLINKS() : null },
                { title: 'Forms, Dues & Registrations', icon: <ListAltIcon className="m-r-20px" />, link: '/portal/forms', navItems: FORMS_NAVLINKS() },
                { title: 'Events', icon: <SECTION_ICONS.Events className="m-r-20px" />, link: '/portal/events/events' },
                { title: 'Custom', icon: <SECTION_ICONS.Custom className="m-r-20px" />, link: '/portal/custom', navItems: CUSTOM_CONTENTS_NAVLINKS() },
                ...ncrNavItem,
                { title: 'Help', icon: <LiveHelpIcon className="m-r-20px" />, link: '/portal/tutorials', navItems: HELP_NAVLINKS(globalState.websiteType) },
                { title: 'Settings', icon: <SettingsIcon className="m-r-20px" />, link: '/portal/settings', navItems: SETTINGS_NAVLINKS() },
            ];
        } if (globalState.websiteType === WebsiteType.WEBSITEANDSTORE) {
            return [
                { title: 'Dashboard', icon: <DashboardIcon className="m-r-20px" />, link: '/portal/dashboard' },
                { title: 'Analytics', icon: <TimelineIcon className="m-r-20px" />, link: '/portal/analytics', navItems: ANALYTICS_NAVLINKS() },
                { title: 'Website', icon: <WebIcon className="m-r-20px" />, link: '/portal/website', navItems: WEBSITE_NAVLINKS(globalState.websiteType) },
                { title: 'Store, Tickets & Registration Forms', icon: <AttachMoneyIcon className="m-r-20px" />, link: '/portal/store', navItems: isValidString(globalState.stripeAccountId) ? STORE_NAVLINKS() : null },
                { title: 'Player Dues & Forms', icon: <ListAltIcon className="m-r-20px" />, link: '/portal/forms', navItems: FORMS_NAVLINKS() },
                { title: 'Seasons & Games', icon: <SECTION_ICONS.Seasons className="m-r-20px" />, link: '/portal/seasons' },
                { title: 'Events & Practice', icon: <SECTION_ICONS.Events className="m-r-20px" />, link: '/portal/events', navItems: EVENTS_NAVLINKS() },
                { title: 'Articles', icon: <SECTION_ICONS.Articles className="m-r-20px" />, link: '/portal/articles', navItems: ARTICLES_NAVLINKS() },
                { title: 'Galleries', icon: <SECTION_ICONS.Galleries className="m-r-20px" />, link: '/portal/galleries', navItems: GALLERIES_NAVLINKS() },
                { title: 'Roster', icon: <SECTION_ICONS.Roster className="m-r-20px" />, link: '/portal/roster', navItems: ROSTER_NAVLINKS() },
                { title: 'Coaches & Staff', icon: <SECTION_ICONS.Coaches className="m-r-20px" />, link: '/portal/coaches' },
                { title: 'Executive Board', icon: <SECTION_ICONS.ExecutiveBoard className="m-r-20px" />, link: '/portal/executiveboard' },
                { title: 'Opponents', icon: <SECTION_ICONS.Opponents className="m-r-20px" />, link: '/portal/opponents' },
                { title: 'Leagues', icon: <SECTION_ICONS.Leagues className="m-r-20px" />, link: '/portal/leagues' },
                { title: 'Sponsors', icon: <SECTION_ICONS.Sponsors className="m-r-20px" />, link: '/portal/sponsors' },
                { title: 'Custom', icon: <SECTION_ICONS.Custom className="m-r-20px" />, link: '/portal/custom', navItems: CUSTOM_CONTENTS_NAVLINKS() },
                ...ncrNavItem,
                { title: 'Help', icon: <LiveHelpIcon className="m-r-20px" />, link: '/portal/tutorials', navItems: HELP_NAVLINKS(globalState.websiteType) },
                { title: 'Settings', icon: <SettingsIcon className="m-r-20px" />, link: '/portal/settings', navItems: SETTINGS_NAVLINKS() },
            ];
        }
        return [];
    };

    const DRAWER_VISBLE_URL_SEGMENTS = ['portal'];
    const firstSegment = history.location.pathname.split('/')[1];

    const fetchTeamLogo = () => {
        if (isValidObject(globalState) && isValidObject(globalState.teamObject) && isValidObject(globalState.teamObject.silhouetteLogo) && isValidObject(globalState.teamObject.silhouetteLogo.logo) && isValidObject(globalState.teamName)) {
            return (<img className="side-drawer-team-logo p-t-10px p-b-10px p-l-10px p-r-10px x-center m-t-20px" src={globalState.teamObject.silhouetteLogo.logo} alt={globalState.teamName || 'Team Logo'} />);
        }
        return null;
    };

    return (
        <>
            {DRAWER_VISBLE_URL_SEGMENTS.includes(firstSegment) ? (
                <Drawer className="side-drawer" variant="permanent" open>
                    {fetchTeamLogo()}
                    <a href={getWebsiteURL(globalState)} target="_blank">
                        <Button className="side-drawer-team-website-button button font-size-m m-t-10px m-b-10px">
                            Visit your Website
                        </Button>
                    </a>
                    <nav className="side-drawer-nav-items full-width m-t-10px">
                        {getNavItems().map((item) => {
                            return (
                                <React.Fragment key={item.link}>
                                    {isValidObject(item.navItems) ? (
                                        <Accordion
                                            expanded={state.expandedAccordian.startsWith(item.link)}
                                            onChange={
                                                () => {
                                                    setState({ type: 'setExpandedAccordian', expandedAccordian: item.link });
                                                    if (!history.location.pathname.startsWith(item.link)) {
                                                        history.push(item.link);
                                                    }
                                                }
                                            }
                                            className="side-drawer-accordian-nav-item"
                                        >
                                            <TooltipComponent3
                                                title={(
                                                    <>
                                                        {item.navItems.map((navItem) => {
                                                            return (
                                                                <NavLink className="text-no-underline" key={navItem.title} to={navItem.link} onClick={() => { setState({ type: 'setExpandedAccordian', expandedAccordian: navItem.link }); }}>
                                                                    <p className="x-center font-size-l font-weight-400 full-width text-left-align p-r-10px text-white text-lightgray-hover m-t-0 m-b-0">
                                                                        {navItem.title}
                                                                    </p>
                                                                </NavLink>
                                                            );
                                                        })}
                                                    </>
                                                )}
                                                placement="right"
                                                interactive
                                            >
                                                <AccordionSummary className={`side-drawer-accordian-nav-item-summary ${history.location.pathname.startsWith(item.link) ? 'side-drawer-accordian-nav-item-summary-active' : ''}`}>
                                                    <p className="x-center font-size-xl font-weight-400 full-width text-left-align p-r-10px">
                                                        {item.icon}
                                                        {item.title}
                                                    </p>
                                                </AccordionSummary>
                                            </TooltipComponent3>
                                            <AccordionDetails className="side-drawer-accordian-nav-item-details">
                                                {item.navItems.map((navItem) => {
                                                    return (
                                                        <NavLink activeClassName="side-drawer-nav-item-active x-center" className="side-drawer-nav-item text-no-underline x-center" key={navItem.title} to={navItem.link} onClick={() => { setState({ type: 'setExpandedAccordian', expandedAccordian: navItem.link }); }}>
                                                            <p className="x-center font-size-l font-weight-400 full-width text-left-align p-r-10px">
                                                                {navItem.title}
                                                            </p>
                                                        </NavLink>
                                                    );
                                                })}
                                            </AccordionDetails>
                                        </Accordion>
                                    ) : (
                                        <NavLink activeClassName="side-drawer-nav-item-active x-center" className={`side-drawer-nav-item text-no-underline x-center ${item.title === 'Store, Tickets & Registration Forms' ? 'side-drawer-nav-item-green' : ''}`} key={item.title} to={item.link} onClick={() => { setState({ type: 'setExpandedAccordian', expandedAccordian: item.link }); }}>
                                            <p className="x-center font-size-xl font-weight-400 full-width text-left-align p-r-10px">
                                                {item.icon}
                                                {item.title}
                                            </p>
                                        </NavLink>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </nav>
                    <div className="p-b-5" />
                    {/* <div className="side-drawer-optimx x-center m-b-20px full-width">
                        <img src={OptimXSportsIcon} className="side-drawer-optimx-logo x-center full-width" alt="OptimX Sports" />
                        <p className="side-drawer-optimx-text text-no-underline text-white font-size-3xl full-width">OPTIMXSPORTS</p>
                    </div> */}
                </Drawer>
            ) : null}
            {children}
        </>
    );
};

ExpandedDrawer.propTypes = {
    children: PropTypes.node.isRequired,
};

export default memo(ExpandedDrawer);
